import { State, Action, StateContext, Selector } from '@ngxs/store';
import { PayrollService } from './service';
import { IPayrollState, IPayroll, IChartEntry, IPaymentAuth } from './types';
import {
    FetchOne,
    FetchChart,
    SetSendNotificationsID,
    UpdateOne,
    AmendTimesheet,
    SetCurrentTab,
    SetSearchField,
    SetFiltersBasedOnTab,
    SetCurrentTimesheetTab,
    SetCurrentPaymentTab,
    AuthPayments,
    ValidateAuthPayments,
    SetAmendPayroll,
} from './actions';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { TokenService } from '../../../../core/services/token.service';
import { IPagination } from '../../../authorization/store/types';
import { of, Observable } from 'rxjs';
import { isValid } from 'date-fns';

const defaults: IPayrollState = {
    isLoading: false,
    currentTab: null,
    searchField: '',
    generalPayrollsFilters: {
        page: 1,
        results: 100,
        sort: 'createdDESC',
        companyID: null,
        unpaidPayslips: null,
    },
    dashboardPayrollsFilters: {
        page: 1,
        results: 10,
        sort: 'createdDESC',
        companyID: null,
    },
    generalPayrolls: null,
    dashboardPayrolls: null,
    sendNotificationsID: null,
    amendPayroll: null,
    chart: null,
    currentPayroll: null,
    currentTimesheetTab: null,
    currentPaymentTab: 'GENERAL',
    authPayment: null,
};

import * as Sentry from '@sentry/browser';

@State<IPayrollState>({
    name: 'payrolls',
    defaults,
})
@Injectable()
export class PayrollState {
    @Selector()
    static isLoading(state: IPayrollState) {
        return state.isLoading;
    }

    @Selector()
    static currentTab(state: IPayrollState) {
        return state.currentTab;
    }

    @Selector()
    static authPayment(state: IPayrollState) {
        return state.authPayment;
    }

    @Selector()
    static searchField(state: IPayrollState) {
        return state.searchField;
    }

    @Selector()
    static generalPayrollsFilters(state: IPayrollState) {
        return state.generalPayrollsFilters;
    }

    @Selector()
    static dashboardPayrollsFilters(state: IPayrollState) {
        return state.dashboardPayrollsFilters;
    }

    @Selector()
    static generalPayrolls(state: IPayrollState) {
        return state.generalPayrolls;
    }

    @Selector()
    static dashboardPayrolls(state: IPayrollState) {
        return state.dashboardPayrolls;
    }

    @Selector()
    static sendNotificationsID(state: IPayrollState) {
        return state.sendNotificationsID;
    }

    @Selector()
    static amendPayroll(state: IPayrollState) {
        return state.amendPayroll;
    }

    @Selector()
    static chart(state: IPayrollState) {
        return state.chart;
    }

    @Selector()
    static currentPayroll(state: IPayrollState) {
        return state.currentPayroll;
    }

    @Selector()
    static currentTimesheetTab(state: IPayrollState) {
        return state.currentTimesheetTab;
    }

    @Selector()
    static currentPaymentTab(state: IPayrollState) {
        return state.currentPaymentTab;
    }

    constructor(private tokenService: TokenService, private payrollService: PayrollService) {}

    ngxsOnInit(ctx: StateContext<IPayrollState>): void {
        ctx.dispatch(new ValidateAuthPayments());
    }

    @Action(SetCurrentPaymentTab)
    SetCurrentPaymentTab(ctx: StateContext<IPayrollState>, action: SetCurrentPaymentTab) {
        const state = ctx.getState();
        ctx.patchState({ currentPaymentTab: action.tab || state.currentPaymentTab || 'GENERAL' });
    }

    @Action(SetCurrentTimesheetTab)
    SetCurrentTimesheetTab(ctx: StateContext<IPayrollState>, action: SetCurrentTimesheetTab) {
        const state = ctx.getState();
        ctx.patchState({ currentTimesheetTab: action.tab || state.currentTimesheetTab || 'WORKED' });
    }

    @Action(SetCurrentTab)
    SetCurrentTab(ctx: StateContext<IPayrollState>, action: SetCurrentTab) {
        const state = ctx.getState();
        ctx.patchState({ currentTab: action.tab || state.currentTab || 'GENERAL' });
        ctx.dispatch(new SetFiltersBasedOnTab({ page: 1, ...action.filters }));
    }

    @Action(SetSearchField)
    SetSearchField(ctx: StateContext<IPayrollState>, action: SetSearchField) {
        ctx.patchState({ searchField: action.value });
        ctx.dispatch(new SetFiltersBasedOnTab({ page: 1 }));
    }

    @Action(SetFiltersBasedOnTab)
    SetFiltersBasedOnTab(ctx: StateContext<IPayrollState>, action: SetFiltersBasedOnTab) {
        let state = ctx.getState();
        ctx.patchState({ isLoading: true });
        switch (state.currentTab) {
            case 'DASHBOARD':
                ctx.patchState({
                    dashboardPayrollsFilters: {
                        ...state.dashboardPayrollsFilters,
                        ...action.filters,
                    },
                });
                state = ctx.getState();
                return this.payrollService
                    .fetchAll({ ...state.dashboardPayrollsFilters, search: state.searchField })
                    .pipe(tap((pagination: IPagination<IPayroll>) => ctx.patchState({ dashboardPayrolls: pagination, isLoading: false })));
            default:
                ctx.patchState({
                    generalPayrollsFilters: {
                        ...state.generalPayrollsFilters,
                        ...action.filters,
                    },
                });
                state = ctx.getState();
                return this.payrollService
                    .fetchAll({ ...state.generalPayrollsFilters, search: state.searchField })
                    .pipe(tap((pagination: IPagination<IPayroll>) => ctx.patchState({ generalPayrolls: pagination, isLoading: false })));
        }
    }

    @Action(SetSendNotificationsID)
    SetSendNotificationsID(ctx: StateContext<IPayrollState>, action: SetSendNotificationsID) {
        return ctx.patchState({ sendNotificationsID: action.id });
    }

    @Action(SetAmendPayroll)
    SetAmendPayroll(ctx: StateContext<IPayrollState>, action: SetAmendPayroll) {
        return ctx.patchState({ amendPayroll: action.id });
    }

    @Action(FetchChart)
    FetchChart(ctx: StateContext<IPayrollState>, action: FetchChart) {
        return this.payrollService.fetchChart(action.companyID).pipe(tap((chartEntries: IChartEntry[]) => ctx.patchState({ chart: chartEntries })));
    }

    @Action(FetchOne)
    FetchOne(ctx: StateContext<IPayrollState>, action: FetchOne) {
        const state = ctx.getState();
        if (state.currentPayroll) {
            ctx.patchState({ currentPayroll: null });
        }
        return this.payrollService.fetchOne(action.id).pipe(tap((payroll: IPayroll) => ctx.patchState({ currentPayroll: payroll })));
    }

    @Action(UpdateOne)
    UpdateOne(ctx: StateContext<IPayrollState>, action: UpdateOne) {
        const state = ctx.getState();
        return this.payrollService.update(action.id, action.data).pipe(
            tap(() =>
                ctx.patchState({
                    currentPayroll: {
                        ...state.currentPayroll,
                        ...action.data,
                    },
                }),
            ),
        );
    }

    @Action(AmendTimesheet)
    AmendTimesheet(ctx: StateContext<IPayrollState>, action: AmendTimesheet) {
        const state = ctx.getState();
        ctx.patchState({
            currentPayroll: {
                ...state.currentPayroll,
                ...{ payment_processed_date: null },
            },
        });
    }

    @Action(AuthPayments)
    AuthPayments(ctx: StateContext<IPayrollState>, action: AuthPayments) {
        Sentry.addBreadcrumb({
            category: 'payroll authPayments',
            message: 'Used code ' + action.code,
            level: 'info',
        });
        return this.payrollService.paymentsAuthenticateConfirm(action.code).pipe(
            tap((authPayment: IPaymentAuth) => {
                Sentry.addBreadcrumb({
                    category: 'payroll authPayments result',
                    message: JSON.stringify(authPayment),
                    level: 'info',
                });
                this.tokenService.addPaymentsToken(authPayment);
                ctx.patchState({ authPayment });
            }),
        );
    }

    @Action(ValidateAuthPayments)
    ValidateAuthPayments(ctx: StateContext<IPayrollState>): void {
        try {
            const authPayments = this.tokenService.getPaymentsToken();

            if (!authPayments) {
                ctx.patchState({ authPayment: null });
                this.tokenService.removePaymentsToken();
            } else if (
                !isValid(new Date(authPayments.token_expiration_unix)) ||
                new Date(authPayments.token_expiration_unix).getTime() < new Date().getTime()
            ) {
                this.tokenService.removePaymentsToken();
                ctx.patchState({ authPayment: null });
            } else {
                this.tokenService.addPaymentsToken(authPayments);
                ctx.patchState({ authPayment: authPayments });
            }
        } catch (err) {
            this.tokenService.removePaymentsToken();
            ctx.patchState({ authPayment: null });
            console.error(err);
        }
    }
}
